export const HITLAB_RADIO_ROUTE = '/hitlab/radio';

export const RADIO_QUEUE_KINDS = Object.freeze({
  TRACK: 'track',
  BUMPER: 'bumper',
  AUDIO_AD: 'audio_ad',
  CLICKABLE_MEDIA: 'clickable_media',
});

export const EMPTY_RADIO_QUEUE = Object.freeze([]);

const V1_RENDERABLE_KINDS = new Set([
  RADIO_QUEUE_KINDS.TRACK,
  RADIO_QUEUE_KINDS.BUMPER,
]);

const cleanCatalogText = (value, max = 256) =>
  String(value || '').trim().slice(0, max);

const toCatalogNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeSupportText = (value, max = 160) =>
  cleanCatalogText(value, max);

const hasSupportShape = (item = {}) =>
  Object.prototype.hasOwnProperty.call(item, 'canTipWithJgk') ||
  Object.prototype.hasOwnProperty.call(item, 'canBuyWithJgk') ||
  Object.prototype.hasOwnProperty.call(item, 'canBuySupportWithJgk') ||
  Object.prototype.hasOwnProperty.call(item, 'tipUnavailableReason') ||
  Object.prototype.hasOwnProperty.call(item, 'buyUnavailableReason');

const getSupportFields = (item = {}, isTrack = false) => {
  const supportShape = hasSupportShape(item);
  const canBuySupportWithJgk =
    item.canBuySupportWithJgk === true || item.canBuyWithJgk === true;
  return {
    hasJgkSupportFields: supportShape,
    canTipWithJgk: isTrack && item.canTipWithJgk === true,
    tipUnavailableReason: normalizeSupportText(item.tipUnavailableReason),
    canBuyWithJgk: isTrack && item.canBuyWithJgk === true,
    canBuySupportWithJgk: isTrack && canBuySupportWithJgk,
    buyPriceJgk:
      item.buyPriceJgk === null || item.buyPriceJgk === undefined
        ? null
        : Math.max(0, Math.round(toCatalogNumber(item.buyPriceJgk, 0))),
    buyUnavailableReason: normalizeSupportText(item.buyUnavailableReason),
    alreadyPurchased: isTrack && item.alreadyPurchased === true,
    downloadAvailable: isTrack && item.downloadAvailable === true,
  };
};

const normalizeBackendCatalogItem = (item = {}) => {
  const trackId = cleanCatalogText(item.trackId, 120);
  const kind = cleanCatalogText(item.kind, 40);
  const title = cleanCatalogText(item.title, 160);
  const audioUrl = cleanCatalogText(item.audioUrl, 2048);
  if (!trackId || !audioUrl) return null;

  if (kind === RADIO_QUEUE_KINDS.TRACK) {
    const artistName = cleanCatalogText(item.artistName, 120);
    const coverArtUrl = cleanCatalogText(item.coverArtUrl, 2048);
    if (!title || !artistName || !coverArtUrl) return null;
    return {
      id: trackId,
      kind: RADIO_QUEUE_KINDS.TRACK,
      artistName,
      linkedArtistPlayerId: cleanCatalogText(item.linkedArtistPlayerId, 120),
      songTitle: title,
      audioUrl,
      coverArtUrl,
      artistInstagramUrl: '',
      artistProfileUrl: '',
      rightsStatus: 'rights_clean',
      rotationStatus: 'active',
      durationSeconds: Math.max(0, toCatalogNumber(item.durationSeconds)),
      displayOrder: toCatalogNumber(item.displayOrder),
      feedbackEligible: true,
      isNewInRotation: item.isNewInRotation === true,
      newInRotationUntil: cleanCatalogText(item.newInRotationUntil, 40),
      isFreshRotationSignalEligible:
        item.isFreshRotationSignalEligible === true,
      newRotationMessage: cleanCatalogText(item.newRotationMessage, 220),
      supportEligible: item.canTipWithJgk === true || item.canBuyWithJgk === true,
      ...getSupportFields(item, true),
      isBackendCatalogItem: true,
    };
  }

  if (kind === RADIO_QUEUE_KINDS.BUMPER) {
    const label = cleanCatalogText(item.publicLabel || item.title, 160);
    if (!label) return null;
    return {
      id: trackId,
      kind: RADIO_QUEUE_KINDS.BUMPER,
      title,
      label,
      audioUrl,
      rightsStatus: 'rights_clean',
      rotationStatus: 'active',
      durationSeconds: Math.max(0, toCatalogNumber(item.durationSeconds)),
      displayOrder: toCatalogNumber(item.displayOrder),
      feedbackEligible: false,
      supportEligible: false,
      ...getSupportFields(item, false),
      isBackendCatalogItem: true,
    };
  }

  return null;
};

const isActiveRenderableItem = (item) =>
  item &&
  V1_RENDERABLE_KINDS.has(item.kind) &&
  item.rotationStatus === 'active' &&
  item.rightsStatus === 'rights_clean' &&
  Boolean(item.audioUrl) &&
  (item.kind !== RADIO_QUEUE_KINDS.TRACK || Boolean(item.coverArtUrl));

export const mergeRadioCatalogItems = (
  catalogItems = EMPTY_RADIO_QUEUE,
  legacyBackendItems,
) => {
  const sourceItems = Array.isArray(legacyBackendItems)
    ? legacyBackendItems
    : catalogItems;

  return (Array.isArray(sourceItems) ? sourceItems : [])
    .map(normalizeBackendCatalogItem)
    .filter(Boolean)
    .sort((a, b) => {
      const orderDiff =
        toCatalogNumber(a.displayOrder) - toCatalogNumber(b.displayOrder);
      if (orderDiff) return orderDiff;
      return a.id.localeCompare(b.id);
    })
    .filter(isActiveRenderableItem);
};

export const normalizeRadioCatalogResponseItems = (response) => {
  const items = response?.data?.data?.items;
  return Array.isArray(items) ? items : [];
};

export const getActiveRadioQueue = (queue = EMPTY_RADIO_QUEUE) =>
  (Array.isArray(queue) ? queue : []).filter(isActiveRenderableItem);

export const getActiveRadioTracks = (queue = EMPTY_RADIO_QUEUE) =>
  getActiveRadioQueue(queue).filter(
    (item) => item.kind === RADIO_QUEUE_KINDS.TRACK,
  );
